import {
  DefaultNodeTypes,
  SerializedBlockNode,
  SerializedLinkNode,
} from '@payloadcms/richtext-lexical';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import {
  JSXConvertersFunction,
  LinkJSXConverter,
  RichText as RichTextWithoutBlocks,
} from '@payloadcms/richtext-lexical/react';

import { CodeBlock, CodeBlockProps } from '@/blocks/Code/Component';
import { MediaBlock } from '@/blocks/MediaBlock/Component';
import { ModalFormButton } from '@/blocks/ModalFormButton/Component';
import { Link } from '@/blocks/Link/Component';

import type { MediaBlock as MediaBlockProps } from '@/payload-types';
import { cn } from '@/utilities/cn';
import { NAV } from '@/constants';

type NodeTypes =
  | DefaultNodeTypes
  | SerializedBlockNode<MediaBlockProps | CodeBlockProps>;

const internalDocToHref = ({ linkNode }: { linkNode: SerializedLinkNode }) => {
  const { value, relationTo } = linkNode.fields.doc!;
  if (typeof value !== 'object') {
    throw new Error('Expected value to be an object');
  }
  const slug = value.slug as string;
  return relationTo === 'posts'
    ? NAV.blogDetail(slug)
    : relationTo === 'posts'
      ? NAV.serviceDetail(slug)
      : `/${slug}`;
};

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({
  defaultConverters,
}) => ({
  ...defaultConverters,
  ...LinkJSXConverter({ internalDocToHref }),
  blocks: {
    mediaBlock: ({ node }) => (
      <MediaBlock
        imgClassName="m-0 corners"
        {...node.fields}
        captionClassName="mx-auto max-w-[48rem]"
        enableGutter={false}
        disableInnerContainer={true}
      />
    ),
    code: ({ node }) => <CodeBlock className="col-start-2" {...node.fields} />,
    link: ({ node }) => <Link className="mt-5 md:mt-3" {...node.fields} />,
    modalFormButton: ({ node }) => <ModalFormButton {...node.fields} />,
  },
});

type Props = {
  data: SerializedEditorState;
  enableGutter?: boolean;
  enableProse?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export function RichText(props: Props) {
  const {
    className,
    enableProse = true,
    enableGutter = false,
    ...rest
  } = props;
  return (
    <RichTextWithoutBlocks
      converters={jsxConverters}
      className={cn(
        'prose-headings:text-inherit prose-headings:last:!mb-0',
        {
          container: enableGutter,
          'max-w-none': !enableGutter,
          'md:prose-md prose': enableProse,
          // mx-auto
        },
        className,
      )}
      {...rest}
    />
  );
}
